import React, { createContext, useState, useContext, useEffect } from "react";
import { useToast } from "@/components/ui/use-toast";
import { detectBrowserLanguage } from "@/lib/language-utils";

// Types des langues disponibles
export type Language = "fr" | "en" | "es" | "it" | "de" | "nl";

interface LanguageContextType {
  language: Language;
  setLanguage: (language: Language) => void;
  translations: Record<string, Record<Language, string>>;
  t: (key: string) => string;
}

// Créer le contexte avec des valeurs par défaut
const LanguageContext = createContext<LanguageContextType>({
  language: "fr",
  setLanguage: () => {},
  translations: {},
  t: (key: string) => key,
});

// Hook personnalisé pour utiliser le contexte de langue
export const useLanguage = () => useContext(LanguageContext);

interface LanguageProviderProps {
  children: React.ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  // État pour stocker la langue actuelle
  const [language, setLanguageState] = useState<Language>("fr");
  const { toast } = useToast();
  
  // Fonction pour définir la langue et la stocker dans localStorage
  const setLanguage = (newLanguage: Language) => {
    setLanguageState(newLanguage);
    localStorage.setItem("preferred-language", newLanguage);
    
    // Afficher une notification de confirmation
    toast({
      title: translations.languageChanged[newLanguage],
      description: translations.languageChangedDescription[newLanguage],
      duration: 3000,
    });
  };
  
  // Effet pour détecter la langue du navigateur au premier chargement
  useEffect(() => {
    // Vérifier d'abord si l'utilisateur a déjà une préférence stockée
    const storedLanguage = localStorage.getItem("preferred-language") as Language | null;
    
    if (storedLanguage && ["fr", "en", "es", "it", "de", "nl"].includes(storedLanguage)) {
      setLanguageState(storedLanguage);
    } else {
      // Sinon, essayer de détecter la langue du navigateur
      const detectedLang = detectBrowserLanguage();
      
      // Ne pas afficher la popup si déjà la langue par défaut (français)
      if (detectedLang !== "fr" && ["en", "es", "it", "de", "nl"].includes(detectedLang)) {
        // Nous ne changeons pas automatiquement la langue, juste stockons la langue détectée
        localStorage.setItem("detected-language", detectedLang);
      }
    }
  }, []);
  
  // Traductions pour l'UI
  const translations = {
    // Ajout de la langue actuelle comme clé de traduction
    language: {
      fr: "fr",
      en: "en",
      es: "es",
      it: "it",
      de: "de",
      nl: "nl",
    },
    // Navbar
    home: {
      fr: "Accueil",
      en: "Home",
      es: "Inicio",
      it: "Home",
      de: "Startseite",
      nl: "Home",
    },
    gallery: {
      fr: "Galerie",
      en: "Gallery",
      es: "Galería",
      it: "Galleria",
      de: "Galerie",
      nl: "Galerij",
    },
    equipment: {
      fr: "Équipements",
      en: "Equipment",
      es: "Equipamiento",
      it: "Attrezzature",
      de: "Ausstattung",
      nl: "Uitrusting",
    },
    booking: {
      fr: "Réserver",
      en: "Book Now",
      es: "Reservar",
      it: "Prenota",
      de: "Buchen",
      nl: "Boeken",
    },
    activities: {
      fr: "Activités",
      en: "Activities",
      es: "Actividades",
      it: "Attività",
      de: "Aktivitäten",
      nl: "Activiteiten",
    },
    contact: {
      fr: "Contact",
      en: "Contact",
      es: "Contacto",
      it: "Contatto",
      de: "Kontakt",
      nl: "Contact",
    },
    myReservations: {
      fr: "Mes Réservations",
      en: "My Reservations",
      es: "Mis Reservas",
      it: "Le Mie Prenotazioni",
      de: "Meine Buchungen",
      nl: "Mijn Reserveringen",
    },
    profile: {
      fr: "Profil",
      en: "Profile",
      es: "Perfil",
      it: "Profilo",
      de: "Profil",
      nl: "Profiel",
    },
    logIn: {
      fr: "Connexion",
      en: "Log In",
      es: "Iniciar Sesión",
      it: "Accedi",
      de: "Anmelden",
      nl: "Inloggen",
    },
    logOut: {
      fr: "Déconnexion",
      en: "Log Out",
      es: "Cerrar Sesión",
      it: "Disconnettiti",
      de: "Abmelden",
      nl: "Uitloggen",
    },
    // Footer
    access: {
      fr: "Accès",
      en: "Access",
      es: "Acceso",
      it: "Accesso",
      de: "Anfahrt",
      nl: "Toegang",
    },
    surroundings: {
      fr: "Environs",
      en: "Surroundings",
      es: "Alrededores",
      it: "Dintorni",
      de: "Umgebung",
      nl: "Omgeving",
    },
    legalNotice: {
      fr: "Mentions Légales",
      en: "Legal Notice",
      es: "Aviso Legal",
      it: "Note Legali",
      de: "Impressum",
      nl: "Juridische Kennisgeving",
    },
    // Language Dialog
    languageSwitchTitle: {
      fr: "Changer de langue",
      en: "Change Language",
      es: "Cambiar Idioma",
      it: "Cambia Lingua",
      de: "Sprache ändern",
      nl: "Taal wijzigen",
    },
    languageSwitchDescription: {
      fr: "Nous avons détecté que votre langue préférée pourrait être différente. Souhaitez-vous consulter le site dans une autre langue ?",
      en: "We've detected that your preferred language might be different. Would you like to view the site in another language?",
      es: "Hemos detectado que su idioma preferido podría ser diferente. ¿Le gustaría ver el sitio en otro idioma?",
      it: "Abbiamo rilevato che la tua lingua preferita potrebbe essere diversa. Vorresti visualizzare il sito in un'altra lingua?",
      de: "Wir haben festgestellt, dass Ihre bevorzugte Sprache möglicherweise eine andere ist. Möchten Sie die Website in einer anderen Sprache anzeigen?",
      nl: "We hebben gedetecteerd dat uw voorkeurstaal anders zou kunnen zijn. Wilt u de site in een andere taal bekijken?",
    },
    keepFrench: {
      fr: "Rester en français",
      en: "Keep French",
      es: "Mantener francés",
      it: "Mantieni francese",
      de: "Französisch beibehalten",
      nl: "Frans behouden",
    },
    switchLanguage: {
      fr: "Changer de langue",
      en: "Switch language",
      es: "Cambiar idioma",
      it: "Cambia lingua",
      de: "Sprache wechseln",
      nl: "Taal wijzigen",
    },
    languageChanged: {
      fr: "Langue modifiée",
      en: "Language changed",
      es: "Idioma cambiado",
      it: "Lingua cambiata",
      de: "Sprache geändert",
      nl: "Taal gewijzigd",
    },
    languageChangedDescription: {
      fr: "Le site est maintenant affiché en français",
      en: "The site is now displayed in English",
      es: "El sitio ahora se muestra en español",
      it: "Il sito è ora visualizzato in italiano",
      de: "Die Website wird jetzt auf Deutsch angezeigt",
      nl: "De site wordt nu weergegeven in het Nederlands",
    },
    // Cookies consent
    privacy: {
      fr: "Confidentialité",
      en: "Privacy",
      es: "Privacidad",
      it: "Privacy",
      de: "Datenschutz",
      nl: "Privacy",
    },
    cookiesText: {
      fr: "Nous utilisons des cookies et technologies similaires pour améliorer votre expérience, analyser le trafic et personnaliser le contenu. En cliquant sur \"Accepter tout\", vous consentez à l'utilisation de tous les cookies. Vous pouvez également choisir \"Cookies essentiels uniquement\".",
      en: "We use cookies and similar technologies to improve your experience, analyze traffic and personalize content. By clicking on \"Accept All\", you consent to the use of all cookies. You can also choose \"Essential Cookies Only\".",
      es: "Utilizamos cookies y tecnologías similares para mejorar su experiencia, analizar el tráfico y personalizar el contenido. Al hacer clic en \"Aceptar todo\", usted consiente el uso de todas las cookies. También puede elegir \"Solo cookies esenciales\".",
      it: "Utilizziamo cookie e tecnologie simili per migliorare la tua esperienza, analizzare il traffico e personalizzare i contenuti. Facendo clic su \"Accetta tutto\", acconsenti all'uso di tutti i cookie. Puoi anche scegliere \"Solo cookie essenziali\".",
      de: "Wir verwenden Cookies und ähnliche Technologien, um Ihre Erfahrung zu verbessern, den Verkehr zu analysieren und Inhalte zu personalisieren. Durch Klicken auf \"Alles akzeptieren\" stimmen Sie der Verwendung aller Cookies zu. Sie können auch \"Nur essenzielle Cookies\" wählen.",
      nl: "Wij gebruiken cookies en vergelijkbare technologieën om uw ervaring te verbeteren, verkeer te analyseren en inhoud te personaliseren. Door op \"Alles accepteren\" te klikken, stemt u in met het gebruik van alle cookies. U kunt ook kiezen voor \"Alleen essentiële cookies\".",
    },
    learnMore: {
      fr: "En savoir plus sur notre politique de confidentialité",
      en: "Learn more about our privacy policy",
      es: "Más información sobre nuestra política de privacidad",
      it: "Ulteriori informazioni sulla nostra politica sulla privacy",
      de: "Erfahren Sie mehr über unsere Datenschutzrichtlinie",
      nl: "Meer informatie over ons privacybeleid",
    },
    essentialCookies: {
      fr: "Cookies essentiels uniquement",
      en: "Essential Cookies Only",
      es: "Solo cookies esenciales",
      it: "Solo cookie essenziali",
      de: "Nur essenzielle Cookies",
      nl: "Alleen essentiële cookies",
    },
    acceptAll: {
      fr: "Accepter tout",
      en: "Accept All",
      es: "Aceptar todo",
      it: "Accetta tutto",
      de: "Alles akzeptieren",
      nl: "Alles accepteren",
    },
    // Langue elle-même
    french: {
      fr: "Français",
      en: "French",
      es: "Francés",
      it: "Francese",
      de: "Französisch",
      nl: "Frans",
    },
    english: {
      fr: "Anglais",
      en: "English",
      es: "Inglés",
      it: "Inglese",
      de: "Englisch",
      nl: "Engels",
    },
    spanish: {
      fr: "Espagnol",
      en: "Spanish",
      es: "Español",
      it: "Spagnolo",
      de: "Spanisch",
      nl: "Spaans",
    },
    italian: {
      fr: "Italien",
      en: "Italian",
      es: "Italiano",
      it: "Italiano",
      de: "Italienisch",
      nl: "Italiaans",
    },
    german: {
      fr: "Allemand",
      en: "German",
      es: "Alemán",
      it: "Tedesco",
      de: "Deutsch",
      nl: "Duits",
    },
    dutch: {
      fr: "Néerlandais",
      en: "Dutch",
      es: "Holandés",
      it: "Olandese",
      de: "Niederländisch",
      nl: "Nederlands",
    },
    // Langue détectée
    detectLanguage: {
      fr: "Langue détectée",
      en: "Detected language",
      es: "Idioma detectado",
      it: "Lingua rilevata",
      de: "Erkannte Sprache",
      nl: "Gedetecteerde taal",
    },
    
    // Navigation supplémentaire
    theVilla: {
      fr: "La Villa",
      en: "The Villa",
      es: "La Villa",
      it: "La Villa",
      de: "Die Villa",
      nl: "De Villa",
    },
    
    administration: {
      fr: "Administration",
      en: "Administration",
      es: "Administración",
      it: "Amministrazione",
      de: "Verwaltung",
      nl: "Administratie",
    },
    
    bookNow: {
      fr: "Réserver",
      en: "Book Now",
      es: "Reservar ahora",
      it: "Prenota ora",
      de: "Jetzt buchen",
      nl: "Nu boeken",
    },
  };

  // Fonction de traduction
  const t = (key: string) => {
    if (!translations[key]) {
      console.warn(`Translation key not found: ${key}`);
      return key;
    }
    return translations[key][language] || translations[key].fr || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, translations, t }}>
      {children}
    </LanguageContext.Provider>
  );
};
